import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pinAPI } from '../../../../services/redwolfSdkAPI';
import Modal from '../../../common/components/Modal';
import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import * as Sentry from '@sentry/react';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import Spinner from '../../../common/components/Spinner';
import { notification } from '../../../../utils/constants';
import { openNotification } from '../../../../store/commonSlice';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../utils/localStorage';
import PurchasePinSkeleton from '../../../common/skeletons/PurchasePinSkeleton';

const CreatePurchasePin = () => {
	const { creditCards } = useSelector((state: any) => state.pay);

	const [isChangePurchasePinModalOpen, setIsChangePurchasePinModalOpen] = useState(false);
	const [purchasePin, setPurchasePin] = useState('');
	const [formErrorMessage, setFormErrorMessage] = useState('');
	const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(true);
	const [isPinValidCheck, setIsPinValidCheck] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(true);
	const [isPasswordStrong, setIsPasswordStrong] = useState('');
	const [isPinLocked, setIsPinLocked] = useState(false);
	const pinRef: any = useRef();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const dispatch = useDispatch();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Create Purchase Pin' });

	const handleChange = (event: any) => {
		const regexAllowOnlyNumbers = /^[0-9\b]+$/;
		const { value } = event.target;

		if (value === '' || regexAllowOnlyNumbers.test(event.target.value)) {
			setFormErrorMessage('');
			if (value.length <= 6) {
				setPurchasePin(value);
			}

			if (value.length >= 4 && value.length <= 7) {
				setIsContinueButtonDisabled(false);
			} else {
				setIsContinueButtonDisabled(true);
			}
		}
	};

	const validatePurchasePinBetterSecurity = () => {
		if (isPurchaseHasBetterSecurity(purchasePin) && !isPinValidCheck) {
			setIsPinValidCheck(true);
			setFormErrorMessage('Consider updating your PIN for better security');
			setIsPasswordStrong('weak');
			pinRef.current.focus();
			return false;
		} else {
			if (isPurchaseHasBetterSecurity(purchasePin)) {
				setFormErrorMessage('Consider updating your PIN for better security');
				setIsPasswordStrong('weak');
				pinRef.current.focus();
			} else {
				setFormErrorMessage('');
				setIsPasswordStrong('strong');
			}
			return true;
		}
	};

	const clearFormField = () => {
		setIsChangePurchasePinModalOpen(false);
		setPurchasePin('');
		setFormErrorMessage('');
		setIsPinValidCheck(false);
		setIsContinueButtonDisabled(true);
	};

	const createPurchasePin = (event: any) => {
		event.preventDefault();

		const checkValidPin = validatePurchasePinBetterSecurity();

		if (checkValidPin) {
			setIsSpinnerLoading(true);
			const createPinInfo = { accessToken: token, pin: purchasePin };

			requestRedwolfService(pinAPI.createPin(createPinInfo))
				.then((sdkResponse: any) => {
					setIsSpinnerLoading(false);
					if (sdkResponse.response.description === 'PIN Creation Successful') {
						dispatch(
							openNotification({
								type: 'SUCCESS',
								heading: 'Success',
								body: notification.message.updatePurchasePinSuccess
							})
						);
						clearFormField();

						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CREATE_PURCHASE_PIN', {
							item_title: isPasswordStrong,
							count: purchasePin.length
						});
					} else {
						pinRef.current.focus();
						setFormErrorMessage('Invalid PIN');
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_PIN_WARNING', {
							error: sdkResponse.response.description
						});
						Sentry.captureException(new Error('createPin:' + sdkResponse.response));
					}

					transaction && transaction.end();
				})
				.catch((err: any) => {
					setIsSpinnerLoading(false);
					pinRef.current.focus();
					showTechnicalErrorMessage();
					transaction && transaction.end();
					Sentry.captureException(err);
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_PIN_WARNING', { error: err.message });
				});
		}
	};

	const handleModalClose = () => {
		clearFormField();
	};

	const isPurchaseHasBetterSecurity = (pin: any) => {
		const pattern = new RegExp(
			/^(1234|1111|0000|1212|7777|1004|2000|4444|2222|6969|9999|3333|5555|6666|1122|1313|8888|2001|4321|1010|0909|2580|0007|1818|1230|1984|1986|0070|1985|0987|1000|1231|1987|1999|2468|2002|2323|0123|1123|1233|1357|1221|1324|1988|2112|2121|5150|1024|1112|1224|1969|1225|1235|1982|1983|1001|1978|1979|7410|1020|1223|1974|1975|1977|1980|1981|1029|1121|1213|1973|1976|2020|2345|2424|2525|1515|1970|1972|1989|0001|1023|1414|9876|0101|0907|1245|1966|1967|1971|8520|1964|1968|4545|1318|5678|1011|1124|1211|1963|4200|12345|123456|11111|123123|55555|111111|00000|121212|54321|123321|13579|666666|77777|000000|22222|654321|12321|696969|99999|112233|33333|159753|00700|292513|90210|131313|88888|123654|38317|222222|09876|789456|44444|999999|98765|101010|01234|777777|42069|007007)$/gm
		);
		return pattern.test(pin);
	};

	useEffect(() => {
		const hasPinInfo = { accessToken: token };

		if (hasPinInfo) {
			setIsSkeletonLoading(true);
			requestRedwolfService(pinAPI.hasPin(hasPinInfo))
				.then((response: any) => {
					setIsSkeletonLoading(false);
					if (response.isPinLocked) {
						setIsPinLocked(true);
					}
				})
				.catch((err: any) => {
					setIsSkeletonLoading(false);
					showTechnicalErrorMessage();
					Sentry.captureException(err);
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="acc-comp-row">
			<div className="acc-comp-left validation-error">
				<h2 className="ac-title">
					Purchase PIN
					{isPinLocked && (
						<span className="exclamation-icon error-icon pinlock-section">
							<em aria-hidden="true" className="icon-error item-icon"></em>
						</span>
					)}
				</h2>

				<div className="cnt">
					{isSkeletonLoading ? (
						<PurchasePinSkeleton />
					) : creditCards.length ? (
						<p>Approve purchases on your VIZIO TV using a 4-6 digit PIN.</p>
					) : (
						<p>Add a payment method before creating VIZIO Pay PIN</p>
					)}

					{isPinLocked && (
						<p className="error-info pinlock-section">
							Your purchase PIN is locked. Please update your purchase PIN.
						</p>
					)}
				</div>
			</div>

			<div className="acc-comp-right">
				<button
					className="primary-button-small acc-button"
					disabled={creditCards.length === 0}
					onClick={() => setIsChangePurchasePinModalOpen(true)}
					type="button"
					aria-label="Change Pin">
					CHANGE PIN
				</button>

				<Modal
					containerId="changepin"
					show={isChangePurchasePinModalOpen}
					onHide={handleModalClose}
					heading="Change Purchase PIN"
					containerClasses="changePin changePinModal">
					<>
						<form className="edit-modal-form">
							<p className="popup-subtitle changepin-popup-subtitle" id="subtitle">
								Approve purchases on your VIZIO TV. Enter 4-6 digits.{' '}
							</p>

							<div className="form-field">
								<div className="field">
									<div className="input-box form-group input-label">
										<input
											type="text"
											className="form-control updatepin-input"
											name="update-pin"
											ref={pinRef}
											value={purchasePin}
											id="update-pinnumber"
											maxLength={6}
											required
											aria-required="true"
											autoComplete="off"
											aria-label="Purchase PIN"
											aria-describedby="pin-sequence-alert"
											onChange={handleChange}
										/>
									</div>
								</div>
							</div>

							<div id="pin-sequence-alert" role="alert" aria-live="assertive">
								{formErrorMessage}
							</div>

							<div className="changepin-btnwrap changepinmodal-btnwrap">
								<button
									type="submit"
									id="changePinSubmit"
									className="primary-button"
									value="SAVE"
									disabled={isContinueButtonDisabled}
									onClick={createPurchasePin}>
									Save
								</button>
							</div>
						</form>

						{isSpinnerLoading && <Spinner />}
					</>
				</Modal>
			</div>
		</div>
	);
};

export default CreatePurchasePin;
